// PATH: src/pages/Login.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Card, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { ACCESS_DENIED_MESSAGE, accessOf } from "../auth/access";
import logo from "../assets/logo_120x64.png";
import { appVersion } from "../config/config";
import { tokens } from "../theme/theme";

export default function Login() {
  const { login, logout, status, access } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Session déjà ouverte (cookie partagé .tilmicode.com) mais compte ni admin ni relecteur.
  const deniedExistingSession = status === "authenticated" && access === "none";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setSubmitting(true);
    setError(null);
    try {
      const user = await login(form.get("username") as string, form.get("password") as string);
      if (!user) {
        setError("Identifiant ou mot de passe incorrect.");
        return;
      }
      if (accessOf(user) === "none") {
        await logout();
        setError(ACCESS_DENIED_MESSAGE);
        return;
      }
      navigate("/");
    } catch {
      // 401 du service d'identité : identifiants refusés.
      setError("Identifiant ou mot de passe incorrect.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background: `radial-gradient(1200px 600px at 20% -10%, rgba(68,76,231,.35), transparent 60%), ${tokens.sidebar}`,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 400, p: { xs: 3, sm: 4 }, borderRadius: 4, border: 0, boxShadow: "0 24px 64px rgba(0,0,0,.35)" }}>
        <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box component="img" src={logo} alt="Tilmicode" sx={{ height: 48 }} />
          <Box textAlign="center">
            <Typography variant="h3">Administration des contenus</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Connectez-vous avec un compte administrateur ou relecteur.
            </Typography>
          </Box>
        </Stack>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField name="username" label="Identifiant" autoComplete="username" fullWidth required autoFocus size="medium" />
            <TextField
              name="password"
              label="Mot de passe"
              type="password"
              autoComplete="current-password"
              fullWidth
              required
              size="medium"
            />

            {deniedExistingSession && !error && (
              <Alert severity="warning">{ACCESS_DENIED_MESSAGE} Connectez-vous avec un autre compte.</Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}

            <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth sx={{ minHeight: 44 }}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Se connecter"}
            </Button>
          </Stack>
        </Box>

        <Typography variant="caption" component="div" color="text.disabled" sx={{ mt: 3, textAlign: "center" }}>
          v{appVersion}
        </Typography>
      </Card>
    </Box>
  );
}
